import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Chip,
  MultiSelect,
  PriceBandIcon,
  RadioSelect,
  SearchField,
  Table,
  Typography,
  ViewIcon,
} from '@weareopenr/component-library';
import { CellContext, ExpandedState, createColumnHelper } from '@tanstack/react-table';
import { useAuthWrapper } from '@weareopenr/authkit';
import ActionsMenu from 'src/components/ActionsMenu';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { getChannelLogo, mapVenuesToTableRows } from 'src/pages/Venues/mapVenuesToTable/mapVenuesToTable';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import {
  parseCategoriesParam,
  serializeCategoriesParam,
  venueCategoriesToSelectOptions,
  venuesInSelectedCategories,
} from 'src/utils/venueCategories/mappedVenueCategories';
import { IVenueRow, IVenuesTableRow, VenuesTabProps } from './types';
import styles from './VenuesTab.module.scss';

const EXPAND_ALL = 'expand_all';
const COLLAPSE_ALL = 'collapse_all';

const tableViewOptions = [
  { id: EXPAND_ALL, label: 'Expand All' },
  { id: COLLAPSE_ALL, label: 'Collapse All' },
];

const columnHelper = createColumnHelper<IVenuesTableRow>();

const isVenueRow = (row: IVenuesTableRow): row is IVenueRow => 'channels' in row;

const renderVenueCell = (row: IVenuesTableRow) => (
  <div className={styles['cell-content']}>
    {isVenueRow(row) ? (
      <>
        <Typography>{row.name}</Typography>
        {row.isTrial && <Chip label="Trial Venue" backgroundColour="grape" />}
      </>
    ) : (
      <>
        {getChannelLogo(row.name)}
        <Typography>{row.name}</Typography>
      </>
    )}
  </div>
);

const renderMenuCell = (row: IVenuesTableRow) => {
  if (isVenueRow(row) || !row.menu) return null;

  return (
    <Link
      className={styles['menu-link']}
      title={row.menu.name}
      to={`/menus/${row.menu.menu_id}`}
      data-testid="OptionListItemLinks"
      onClick={(e) => e.stopPropagation()}
    >
      {row.menu.name}
    </Link>
  );
};

const renderChannelValue = (info: CellContext<IVenuesTableRow, string | undefined>) =>
  isVenueRow(info.row.original) ? null : info.renderValue();

const renderActionsCell = (
  row: IVenuesTableRow,
  onView: (venueId: string) => void,
  handleOpenDeletePrompt: (id: string) => void,
) =>
  isVenueRow(row) ? (
    <ActionsMenu onView={() => onView(row.venueId)} onDelete={() => handleOpenDeletePrompt(row.venueId)} />
  ) : null;

const VenuesTab = ({ venues, handleOpenDeletePrompt, isSyncInProgress = false }: VenuesTabProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState<string>('');
  const [tableView, setTableView] = useState<string>(COLLAPSE_ALL);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const { featureFlags } = useAuthWrapper();
  const hasVenueCategories = featureFlags.includes('venue_categories');
  const {
    nestedCategories,
    data,
    isLoading: isCategoriesLoading,
  } = useVenueCategories({
    enabled: hasVenueCategories,
  });

  const categoryOptions = useMemo(() => venueCategoriesToSelectOptions(nestedCategories), [nestedCategories]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>(() =>
    hasVenueCategories ? parseCategoriesParam(new URLSearchParams(location.search)) : [],
  );

  const handleCategoryChange = useCallback(
    (selected: string[]): void => {
      setSelectedCategories(selected);

      const search = serializeCategoriesParam(selected, window.location.search);
      const url = `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
      window.history.replaceState(window.history.state, '', url);
    },
    [location.pathname, location.hash],
  );

  const venuesData: IVenueRow[] = useMemo(() => mapVenuesToTableRows(venues ?? []), [venues]);

  const filteredVenues: IVenuesTableRow[] = useMemo(() => {
    let result = venuesData;

    if (selectedCategories.length > 0) {
      const venueIds = new Set(venuesInSelectedCategories(selectedCategories, data?.categories ?? []));
      result = result.filter((venue) => venueIds.has(venue.venueId));
    }

    const query = searchValue.trim().toLowerCase();
    if (query) {
      result = result.filter((venue) => venue.name.toLowerCase().includes(query));
    }

    return result;
  }, [venuesData, selectedCategories, data?.categories, searchValue]);

  const handleTableViewChange = useCallback((id: string): void => {
    setTableView(id);
    setExpanded(id === EXPAND_ALL ? true : {});
  }, []);

  const onView = useCallback(
    (venueId: string): void => {
      navigate(`/venues/${venueId}`);
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (rowData: IVenuesTableRow) => {
      if (!isVenueRow(rowData)) return false as const;

      return () => onView(rowData.venueId);
    },
    [onView],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Venue',
        cell: (info) => renderVenueCell(info.row.original),
      }),
      columnHelper.accessor('externalVenueId', {
        header: 'IDs',
        cell: renderChannelValue,
      }),
      columnHelper.display({
        id: 'menu',
        header: 'Menu',
        cell: (info) => renderMenuCell(info.row.original),
      }),
      columnHelper.accessor('lastPublishedDate', {
        header: 'Last Publish Date',
        cell: renderChannelValue,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => renderActionsCell(info.row.original, onView, handleOpenDeletePrompt),
        size: 80,
      }),
    ],
    [handleOpenDeletePrompt, onView],
  );

  if (isSyncInProgress) return <SkeletonContainer hiddenTitle />;

  return (
    <div className={styles['venues-tab']}>
      <div className={styles['controls-row']}>
        <SearchField
          placeholder="Search for a venue"
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setSearchValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
          isDisabled={false}
        />
        <RadioSelect
          label="Table View"
          Icon={ViewIcon}
          options={[
            {
              heading: 'Dropdown View:',
              options: tableViewOptions,
              selected: tableView,
              onChange: handleTableViewChange,
            },
          ]}
        />
        {hasVenueCategories && (
          <MultiSelect
            Icon={PriceBandIcon}
            label="Venue categories"
            options={categoryOptions}
            selected={selectedCategories}
            onChange={handleCategoryChange}
            loading={isCategoriesLoading}
            disabled={categoryOptions.length === 0}
          />
        )}
      </div>
      <Table
        additionalConfig={{
          state: { expanded },
          onExpandedChange: setExpanded,
          getRowId: (row) => row.rowId,
        }}
        className={styles['table-fill-height']}
        columns={columns}
        data={filteredVenues}
        getSubRows={(row) => (isVenueRow(row) ? row.channels : undefined)}
        isRowClickable={handleRowClick}
      />
    </div>
  );
};

export default VenuesTab;
